import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import { ArrowUpRightIcon, BotIcon, BoxesIcon, DicesIcon, LibraryIcon, PencilRulerIcon, SearchIcon, UsersIcon, type LucideIcon } from 'lucide-react'
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { permissionsFor, type Permissions } from '../../../shared/permissions'

export const Route = createFileRoute('/_authed/')({ component: Home })

type Tool = { title: string; description: string; icon: LucideIcon; /** Shown only to users granted all of these. */ requires: Permissions } & (
	| { to: '/staff' | '/amp-projects' } // in-app route (typed against the route tree)
	| { href: string } // external link, opens in a new tab
)

// Home search. Add an entry here for every section of the panel or service, with the permission it needs.
const tools: Tool[] = [
	{
		title: 'Staff',
		description: 'Manage staff accounts, roles, bans and sessions.',
		icon: UsersIcon,
		requires: { user: ['list'] },
		to: '/staff'
	},
	{
		title: 'Amp projects',
		description: 'Choose which Amp projects may use the edclub CLI, and with which roles.',
		icon: BotIcon,
		requires: { user: ['set-role'] },
		to: '/amp-projects'
	},
	{
		title: 'Standards',
		description: 'Browse and search state education standards.',
		icon: LibraryIcon,
		requires: { standards: ['read'] },
		href: 'https://standards.edclub.tech'
	},
	{
		title: 'Authoring',
		description: 'Create and edit edclub content.',
		icon: PencilRulerIcon,
		requires: { authoring: ['read'] },
		href: 'https://authoring.edclub.com/'
	},
	{
		title: 'Experience Registry',
		description: 'Browse registered edclub experiences.',
		icon: BoxesIcon,
		requires: { registry: ['read'] },
		href: 'https://registry.edclub.tech'
	},
	{
		title: 'Backgammon',
		description: 'Record backgammon games.',
		icon: DicesIcon,
		requires: {},
		href: 'https://backgammon.edclub.tech'
	}
]

const allowed = (tool: Tool, permissions: Permissions) =>
	Object.entries(tool.requires).every(([resource, actions]) =>
		actions.every((action) => (permissions[resource as keyof Permissions] as string[] | undefined)?.includes(action))
	)

// Shown beside each tool and matched by the search: the route path, or the external link's host.
const address = (tool: Tool) => ('to' in tool ? tool.to : new URL(tool.href).host.replace(/^www\./, ''))

// The input points at the highlighted option by id (aria-activedescendant).
const optionId = (tool: Tool) => `home-tool-${tools.indexOf(tool)}`

function Home() {
	const context = Route.useRouteContext()
	const router = useRouter()
	const [query, setQuery] = useState('')
	const [active, setActive] = useState(0)

	const permissions = permissionsFor(context.user.role)
	const available = tools.filter((tool) => allowed(tool, permissions))
	const needle = query.trim().toLowerCase()
	const matches = available.filter((tool) => [tool.title, tool.description, address(tool)].some((text) => text.toLowerCase().includes(needle)))
	const internal = matches.filter((tool) => 'to' in tool)
	const external = matches.filter((tool) => 'href' in tool)
	// The order the list shows them in, which the arrow keys follow.
	const results = [...internal, ...external]
	const current = results[active]

	useEffect(() => {
		if (!current) return
		// The list scrolls on short screens: keep the highlight in view as the arrow keys move it.
		document.getElementById(optionId(current))?.scrollIntoView({ block: 'nearest' })
		// Links preload their route on hover (defaultPreload: 'intent'). Do the same for the highlight, so Enter opens it at once.
		if ('to' in current) void router.preloadRoute({ to: current.to })
	}, [current, router])

	const open = (tool: Tool) => {
		if ('to' in tool) router.navigate({ to: tool.to })
		else window.open(tool.href, '_blank', 'noreferrer')
	}

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			const step = event.key === 'ArrowDown' ? 1 : -1
			if (results.length) setActive((active + step + results.length) % results.length)
		} else if (event.key === 'Enter' && current) {
			event.preventDefault()
			open(current)
		} else if (event.key === 'Escape') {
			setQuery('')
			setActive(0)
		}
	}

	const hover = (tool: Tool) => setActive(results.indexOf(tool))

	return (
		// Exactly the viewport below the shell's header (h-16) and main padding (p-8 top and bottom), so on short
		// screens the search and footer stay put and only the list scrolls. Under min-h-96 the page scrolls instead.
		<div className="mx-auto flex h-[calc(100dvh_-_8rem)] min-h-96 w-full max-w-3xl flex-col gap-[clamp(1rem,3vh,1.75rem)] pt-[clamp(0rem,6vh,2.5rem)]">
			<header className="grid shrink-0 gap-2 text-center">
				<p className="text-sm font-semibold text-muted-foreground [@media(max-height:36rem)]:hidden">
					{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
				</p>
				<h1 className="text-4xl font-bold tracking-tight text-[#122947] [@media(max-height:36rem)]:text-2xl">
					Where to, {context.user.name.split(' ')[0] || context.user.name}?
				</h1>
			</header>
			<div className="relative shrink-0">
				<SearchIcon className="pointer-events-none absolute top-1/2 left-5 size-5 -translate-y-1/2 text-muted-foreground" />
				<input
					type="search"
					role="combobox"
					aria-label="Search the admin portal"
					aria-expanded
					aria-controls="home-tools"
					aria-autocomplete="list"
					aria-activedescendant={current && optionId(current)}
					autoFocus
					autoComplete="off"
					placeholder="Search tools and pages…"
					value={query}
					onChange={(event) => {
						setQuery(event.target.value)
						setActive(0)
					}}
					onKeyDown={onKeyDown}
					className="h-16 w-full rounded-[14px] border border-input bg-card pr-5 pl-14 text-lg text-[#122947] outline-none placeholder:text-muted-foreground focus-visible:border-[#122947] focus-visible:ring-4 focus-visible:ring-[#122947]/8 [@media(max-height:36rem)]:h-12"
				/>
			</div>
			<div className="flex min-h-0 flex-col rounded-[14px] border bg-card p-2">
				<div id="home-tools" role="listbox" aria-label="Tools" className="grid min-h-0 gap-0.5 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
					<ToolGroup label="In the portal" tools={internal} current={current} onHover={hover} />
					<ToolGroup label="Services" tools={external} current={current} onHover={hover} />
				</div>
				{results.length === 0 && (
					<div className="grid shrink-0 gap-1.5 px-4 py-10 text-center">
						<p className="font-bold text-[#122947]">Nothing matches “{query.trim()}”</p>
						<p className="text-sm text-muted-foreground">Try a name like {available.slice(0, 2).map((tool) => tool.title).join(' or ')}.</p>
					</div>
				)}
				<div className="mt-1.5 flex shrink-0 flex-wrap items-center gap-4 border-t px-3 pt-3 pb-1.5 text-xs text-muted-foreground">
					<Hint keys="↑↓">to move</Hint>
					<Hint keys="↵">to open</Hint>
					<Hint keys="esc">to clear</Hint>
					<span className="ml-auto tabular-nums" aria-live="polite">
						{results.length === 1 ? '1 result' : `${results.length} results`}
					</span>
				</div>
			</div>
		</div>
	)
}

function ToolGroup({ label, tools, current, onHover }: { label: string; tools: Tool[]; current: Tool | undefined; onHover: (tool: Tool) => void }) {
	if (!tools.length) return null
	return (
		<div role="group" aria-label={label} className="grid gap-0.5">
			<p aria-hidden className="px-3 pt-3 pb-1.5 text-xs font-bold tracking-widest text-muted-foreground uppercase">
				{label}
			</p>
			{tools.map((tool) => (
				<ToolOption key={tool.title} tool={tool} selected={tool === current} onHover={() => onHover(tool)} />
			))}
		</div>
	)
}

// Options aren't tab stops: the input moves the highlight (arrow keys) and opens it (Enter). Clicks work as plain links.
function ToolOption({ tool, selected, onHover }: { tool: Tool; selected: boolean; onHover: () => void }) {
	const option = {
		id: optionId(tool),
		role: 'option',
		'aria-selected': selected,
		tabIndex: -1,
		// Only a real pointer move takes the highlight: mouseenter (and Chrome's synthetic mousemove) also fire when the
		// list re-renders or scrolls under a still pointer, which would steal the keyboard's highlight.
		onPointerMove: onHover,
		// scroll-mt-9 keeps a group's label in view when scrolling up to its first option.
		className: cn(
			'flex min-h-15 scroll-mt-9 items-center gap-3.5 rounded-[10px] px-3 py-2.5 text-[#122947] outline-none active:bg-[#e9e9e6]',
			selected && 'bg-[#f1f1ef]'
		)
	}
	// Selection changes only the row background, in a single frame. Anything else that changes with it (a fading or
	// inverted icon chip, a swapped trailing icon) makes the highlight flicker as it moves between rows.
	const content = (
		<>
			<span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#f4f4f4] text-[#122947]">
				<tool.icon className="size-[18px]" strokeWidth={1.75} />
			</span>
			<span className="grid min-w-0 flex-1 gap-0.5">
				<span className="font-semibold">{tool.title}</span>
				<span className="truncate text-sm text-muted-foreground">{tool.description}</span>
			</span>
			<span className="hidden shrink-0 font-mono text-xs text-muted-foreground sm:block">{address(tool)}</span>
			<span className="flex size-4 shrink-0 text-muted-foreground">{'href' in tool && <ArrowUpRightIcon className="size-4" />}</span>
		</>
	)
	return 'to' in tool ? (
		<Link to={tool.to} {...option}>
			{content}
		</Link>
	) : (
		<a href={tool.href} target="_blank" rel="noreferrer" {...option}>
			{content}
		</a>
	)
}

function Hint({ keys, children }: { keys: string; children: ReactNode }) {
	return (
		<span className="flex items-center gap-1.5">
			<kbd className="rounded-md border bg-[#f7f7f7] px-1.5 py-px font-mono text-[11px] text-[#122947]">{keys}</kbd>
			{children}
		</span>
	)
}
